import { afterNextRender, AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, Injector, Input, OnInit, signal, ViewChild } from '@angular/core';
import { View } from 'src/app/components/base.component';
import { AuthService } from 'src/app/services/auth.service';
import { UIStateService } from 'src/app/services/ui-state.service';
import { Channel, Message } from '../../../dto';
import { ChatbarService } from '../../../services/chatbar.service';
import { InboxService } from '../../../services/inbox.service';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
    selector: 'app-message-popup',
    templateUrl: './message-popup.component.html',
    styleUrls: ['./message-popup.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessagePopupComponent extends View implements OnInit, AfterViewInit {

    // Backed by a signal, not a plain @Input() field, because sendMessage()'s "new channel"
    // branch below reassigns this.channel itself (inside a subscribe callback, after the
    // channel is created server-side) - a plain field write there would be invisible to OnPush.
    // The setter/getter pair keeps the external [channel]="..." binding and every internal
    // channel.X read working unchanged.
    private channelState = signal<Channel>(null);
    @Input() set channel(value: Channel) { this.channelState.set(value); }
    // Every message insert/read-mark writes a fresh Channel into InboxService's per-channel
    // signal, while the [channel] input keeps pointing at the Channel this popup was opened
    // with - so the live one is looked up by Id here. A channel not (yet) in the service, such
    // as a new draft chat with a negative temporary Id, falls back to the input.
    private liveChannel = computed(() => {
        const opened = this.channelState();
        return (opened && this.inboxService.allChannelsMapSignal().get(opened.Id)?.()) ?? opened;
    });
    get channel(): Channel { return this.liveChannel(); }

    // Re-evaluates only when a different message becomes the newest, not on every read-mark or
    // other rewrite of the channel.
    private newestMessage = computed(() => this.liveChannel()?.Messages?.[0], { equal: (a, b) => a?.Id === b?.Id });

    // How far from the newest message the list may sit and still be scrolled along with new ones.
    private static readonly NEAR_BOTTOM_PX = 48;

    composeMessage = '';
    minimized = false;
    @ViewChild('chatDisplay', { static: false }) scrollingContainer: ElementRef;

    constructor(private inboxService: InboxService, private chatService: ChatbarService, private notificationService: NotificationService, authService: AuthService, uiStateService: UIStateService, private injector: Injector) {
        super(authService, uiStateService);
        effect(() => {
            const newest = this.newestMessage();
            if (!newest) { return; }
            // Read before this pass renders the new message: the list is column-reverse, so its
            // scrollTop is 0 at the newest message and negative once the user has scrolled up.
            const list: HTMLElement | undefined = this.scrollingContainer?.nativeElement;
            const followNewest = newest.IsMyMessage || !list || Math.abs(list.scrollTop) <= MessagePopupComponent.NEAR_BOTTOM_PX;
            if (followNewest) {
                afterNextRender(() => { if (this.scrollingContainer) { this.scrollingContainer.nativeElement.scrollTop = 0; } }, { injector: this.injector });
            }
        });
    }

    ngOnInit(): void {
    }

    // Inbound messages (and their notifications) that the viewer has not yet acknowledged.
    private unreadInbound = computed(() =>
        (this.liveChannel()?.Messages ?? []).filter(m =>
            !m.IsMyMessage && (!m.Read || (m.targetingNotification && !m.targetingNotification.ReadDate))));

    ngAfterViewInit(): void {
        setTimeout(() => {
            let alertedItems = this.scrollingContainer.nativeElement.getElementsByClassName('alerted-item');
            if (alertedItems?.length) {
                alertedItems[alertedItems.length - 1]?.scrollIntoView({ behavior: 'smooth' });
                setTimeout(() => this.requestNotificationsRead(this.channel.Messages
                    .filter(m => m.targetingNotification)), 500);
            }
            let unreadItems = this.scrollingContainer.nativeElement.getElementsByClassName('unread-item');
            if (unreadItems?.length) {
                setTimeout(() => this.requestMessagesRead(this.channel.Messages
                    .filter(m => !m.Read)), 950);
            }
        }, 700);
    }

    // Message Id -> time a read request for it was sent, one map per endpoint. Both the open-time
    // timers above and the interaction handler below send through these, so the same message is
    // not requested twice while a request is still in flight; entries expire so a failed request
    // is retried by a later trigger.
    private notificationReadRequestedAt = new Map<number, number>();
    private messageReadRequestedAt = new Map<number, number>();
    private static readonly READ_REQUEST_TTL_MS = 5000;

    private withoutRecentRequests(requestedAt: Map<number, number>, messages: Message[], now: number): Message[] {
        const fresh = messages.filter(m => now - (requestedAt.get(m.Id) ?? 0) > MessagePopupComponent.READ_REQUEST_TTL_MS);
        fresh.forEach(m => requestedAt.set(m.Id, now));
        return fresh;
    }

    private requestNotificationsRead(messages: Message[]): void {
        this.inboxService.markNotificationsRead(...this.withoutRecentRequests(this.notificationReadRequestedAt, messages, Date.now()));
    }

    private requestMessagesRead(messages: Message[]): void {
        this.inboxService.markMessagesRead(...this.withoutRecentRequests(this.messageReadRequestedAt, messages, Date.now()));
    }

    // Any interaction inside the popup body reads whatever is unread at that moment; a message
    // arriving afterwards stays unread until the next interaction. The minimize/close buttons
    // are excluded.
    markReadOnInteraction(event: Event): void {
        if ((event.target as HTMLElement | null)?.closest?.('.channel-toggles')) { return; }
        const unread = this.unreadInbound();
        this.requestNotificationsRead(unread.filter(m => m.targetingNotification && !m.targetingNotification.ReadDate));
        this.requestMessagesRead(unread.filter(m => !m.Read));
    }

    toggleChannel(): void {
        this.chatService.CloseChannel(this.channel);
    }

    sendMessage(messageText: string, channel: Channel, replyMessage: number | null): void {
        if (this.channel.Id > 0) {
            const message = new Message(null, null);
            message.ChannelId = channel.Id;
            message.SendDate = new Date(Date.now());
            message.MessageText = messageText;
            message.IsMyMessage = true;
            if (replyMessage) {
                message.ParentMessageId = replyMessage;
            }
            this.inboxService.sendMessage(message);
        } else { //new channel, first message
            const message = new Message(null, null);
            message.MessageText = this.composeMessage;
            message.SendDate = new Date();
            message.IsMyMessage = true;

            this.subscriptions.push(
                this.inboxService.createChannelWithInitialMessage(this.channel, message, true).subscribe(chd => {
                    this.chatService.CloseChannel(this.channel);
                    this.channel = chd;
                    this.chatService.OpenChannel(this.channel);

                })
            );
        }
        this.composeMessage = '';
    }
}
